import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface Slide {
  id: string;
  title: string;
  subtitle: string;
  description: string;
}

interface TextSliderProps {
  data: Slide[];
}

/**
 * Text Slider Component
 * Displays animated text with Framer Motion
 */
const TextSlider: React.FC<TextSliderProps> = ({ data }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Auto-rotate slides every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % data.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [data.length]);

  if (!isMounted || data.length === 0) {
    return null;
  }

  const slide = data[activeIndex];
  const titleWords = slide?.title?.split(' ') || [];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
    exit: { opacity: 0, transition: { duration: 0.3 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
    exit: { opacity: 0, y: -10, transition: { duration: 0.3 } },
  };

  return (
    <div>
      <motion.div
        key={activeIndex}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="relative h-[150px] sm:h-[180px] lg:h-[200px] xl:h-[220px] 2xl:h-[240px] overflow-hidden"
      >
        <div className="w-full h-full">
          {/* Title Words Animation */}
          <div className="relative mb-3">
            <motion.h2 
              variants={itemVariants}
              className="font-bold text-xl leading-tight sm:text-2xl md:text-3xl xl:text-4xl 2xl:text-5xl flex flex-wrap justify-center md:justify-start uppercase"
              style={{ textShadow: '0 2px 10px rgba(255, 255, 255, 0.3)' }}
            >
              {titleWords.map((word, index) => (
                <span
                  key={`${activeIndex}-${index}`}
                  className="inline-block mr-2 whitespace-nowrap uppercase"
                >
                  {word}
                </span>
              ))}
            </motion.h2>
          </div>

          {/* Subtitle */}
          <motion.h3
            variants={itemVariants}
            className="font-bold mb-4 text-lg sm:text-xl lg:text-2xl 2xl:text-3xl sm:mx-auto md:mx-0"
            style={{
              textShadow: '0 0 15px rgba(255, 255, 255, 0.5), 0 0 10px rgba(0, 150, 255, 0.3)',
              background: 'linear-gradient(90deg, #ffffff, #ffffff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
            }}
          >
            {slide?.subtitle}
          </motion.h3>

          {/* Description */}
          <motion.p
            variants={itemVariants}
            className="opacity-90 text-sm leading-snug sm:text-base lg:text-lg 2xl:text-xl mx-auto md:mx-0 max-w-md md:max-w-none"
            style={{
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
              color: 'rgba(255, 255, 255, 0.85)',
            }}
          >
            {slide?.description}
          </motion.p>
        </div>
      </motion.div>

      {/* Pagination Dots - Outside overflow container */}
      <div className="flex gap-3 mt-2">
        {data.map((_, index) => (
          <div key={index} className="relative">
            {/* Animated glow background for active dot */}
            {index === activeIndex && (
              <motion.div
                layoutId="dotGlow"
                className="absolute inset-0 w-3 h-3 rounded-full bg-white/20 blur-md"
                transition={{ duration: 0.3, type: 'spring', stiffness: 300 }}
              />
            )}
            {/* Main dot */}
            <motion.button
              onClick={() => setActiveIndex(index)}
              className={`relative w-3 h-3 rounded-full focus:outline-none transition-all duration-300 ${
                index === activeIndex
                  ? 'bg-white shadow-lg shadow-white/50'
                  : 'bg-white/35 hover:bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
              whileHover={{ scale: 1.3 }}
              whileTap={{ scale: 0.85 }}
              animate={index === activeIndex ? { scale: [1, 1.1, 1] } : {}}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            />
          </div>
        ))}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.5);
          }
        }
      `}</style>
    </div>
  );
};

export default TextSlider;
